<template>
  <div class="manager-scorecard" :class="{ 'has-overflow': hasOverflow }">

    <!-- Section header -->
    <div class="sec-head">
      <div>
        <div class="t">{{ $t('insights.Manager_Scorecard') }}</div>
        <div class="d" v-html="$t('insights.Manager_Scorecard_definition')"></div>
      </div>
      <div class="spacer"></div>
      <button class="model-chip" @click="openSettings">
        <span style="display:flex;flex-direction:column;gap:3px;align-items:flex-start;">
          <span class="mc-label">Scoring model</span>
          <span style="display:flex;align-items:center;gap:9px;">
            <span class="mc-name">{{ presetName }}</span>
            <span class="mc-bar">
              <span
                v-for="c in categories"
                :key="c.key"
                :style="{ flexGrow: catWeightFor(c.key) || undefined, background: catWeightFor(c.key) ? c.color : undefined, display: catWeightFor(c.key) ? 'block' : 'none' }"
              ></span>
            </span>
          </span>
        </span>
        <span class="mc-go">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          Configure
        </span>
      </button>
    </div>

    <!-- Distribution card -->
    <!-- Loading skeleton: the empty data set would otherwise render a flat grey
         track with a row of misleading "0" counts, reading as a real all-zero
         distribution. Instead we preview the bar's structure — five faint band
         segments under a shimmer sweep — so it reads as "building" not "empty". -->
    <div v-if="loading" class="card dist is-loading" aria-busy="true">
      <div class="dist-avg">
        <span class="sk sk-avg"></span>
        <span class="lbl">Avg</span>
      </div>
      <div class="dist-bar-wrap">
        <div class="dist-bar sk-bar">
          <span
            v-for="d in distribution"
            :key="d.key"
            class="seg sk-seg"
            :style="{ background: d.color }"
          ></span>
        </div>
        <div class="dist-legend">
          <span class="item" v-for="d in distribution" :key="d.key">
            <span class="sw" :style="{ background: d.color }"></span>
            <span class="sk sk-count"></span>
            <span class="lgd-lbl">{{ d.label }}</span>
          </span>
        </div>
      </div>
    </div>
    <div v-else class="card dist">
      <div class="dist-avg">
        <span class="n">{{ avgScore }}</span>
        <span class="lbl">Avg</span>
      </div>
      <div class="dist-bar-wrap">
        <div class="dist-bar">
          <el-tooltip
            v-for="d in barSegments"
            :key="d.key"
            placement="top"
            :open-delay="120"
            :content="d.detail"
          >
            <span class="seg" :style="{ flexGrow: d.count, background: d.color }"></span>
          </el-tooltip>
        </div>
        <div class="dist-legend">
          <el-tooltip
            v-for="d in distribution"
            :key="d.key"
            placement="top"
            :open-delay="120"
            :content="d.detail"
          >
            <span class="item">
              <span class="sw" :style="{ background: d.color }"></span>
              <b>{{ d.count }}</b> {{ d.label }}
            </span>
          </el-tooltip>
        </div>
      </div>
    </div>

    <!-- Loading skeleton for the category chips: real chips would all read "0"
         until counts arrive, so we show placeholder pills instead. -->
    <div v-if="loading" class="mg-strip">
      <span v-for="w in chipSkeletonWidths" :key="w" class="mg-chip-sk sk" :style="{ width: w + 'px' }"></span>
    </div>

    <!-- Manager-category filter chips. Restored from the legacy Performance Group
         filter so the labels stay consistent with the rest of the product, shown
         in people.vue's talent-group strip + criteria-bar style. -->
    <div v-else class="mg-strip">
      <button
        type="button"
        class="mg-chip"
        :class="{ active: selectedCategory === null }"
        @click="selectedCategory = null"
      >
        <span class="mg-chip-label">All managers</span>
        <span class="mg-chip-count">{{ rows.length }}</span>
      </button>
      <el-tooltip
        v-for="c in managerCategories"
        :key="c.key"
        :content="c.description"
        placement="top"
        effect="light"
        :open-delay="300"
      >
        <button
          type="button"
          class="mg-chip"
          :class="{ active: selectedCategory === c.key, empty: !c.count }"
          @click="toggleCategory(c.key)"
        >
          <span class="mg-chip-label">{{ c.label }}</span>
          <span class="mg-chip-count">{{ c.count }}</span>
        </button>
      </el-tooltip>
    </div>

    <!-- Active-category criteria bar -->
    <div v-if="selectedCategoryConfig" class="mg-criteria">
      <span class="mg-criteria-icon"><i class="el-icon-info"></i></span>
      <div class="mg-criteria-details">
        <div class="mg-criteria-title">{{ selectedCategoryConfig.label }} · {{ selectedCategoryConfig.count }} {{ selectedCategoryConfig.count === 1 ? 'manager' : 'managers' }}</div>
        <div class="mg-criteria-desc">{{ selectedCategoryConfig.description }}</div>
      </div>
      <button type="button" class="mg-criteria-close" @click="selectedCategory = null" title="Clear filter"><i class="el-icon-close"></i></button>
    </div>

    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-top">
        <el-input v-model="searchText" class="sc-search" :placeholder="$t('global.Search')" clearable :disabled="loading"></el-input>

        <div class="date-control" :class="{ 'is-disabled': loading }">
          <el-dropdown
            trigger="click"
            placement="bottom-start"
            :disabled="loading"
            @command="onDateCommand"
          >
            <span class="header-pill-dropdown date-pill" :class="{ active: hasDateFilter }">
              <i class="el-icon-date date-pill-icon"></i>
              <span class="date-pill-label">{{ dateLabel }}</span>
              <button
                v-if="hasDateFilter"
                type="button"
                class="date-pill-clear"
                title="Reset to past 30 days"
                @click.stop="selectDefaultRange"
              >
                <i class="el-icon-close"></i>
              </button>
              <i v-else class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown" class="happily-dropdown-popper sc-date-menu">
              <el-dropdown-item command="default">
                <span class="sc-date-item" :class="{ sel: !hasDateFilter }">
                  <i class="el-icon-check"></i><span>Past 30 days</span>
                </span>
              </el-dropdown-item>
              <el-dropdown-item command="custom" divided>
                <span class="sc-date-item" :class="{ sel: hasDateFilter }">
                  <i class="el-icon-check"></i><span>Custom range…</span>
                </span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <!-- Anchored, visually-hidden range picker — opened from "Custom range…" so
               the calendar pops directly under the pill. -->
          <el-date-picker
            ref="rangePicker"
            v-model="dateFilterRange"
            class="df-hidden-picker"
            popper-class="sc-range-popper"
            placement="bottom-start"
            type="daterange"
            unlink-panels
            format="MMM d, yyyy"
            value-format="yyyy-MM-dd"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            :editable="false"
            :clearable="false"
            :disabled="loading"
            :picker-options="datePickerOptions"
          ></el-date-picker>
        </div>

        <div class="toolbar-actions">
          <span v-if="loading" class="sk sk-count-lg"></span>
          <span v-else class="count">{{ filteredRows.length }} {{ $t('insights.managers') }}</span>
          <button class="sc-export" @click="$emit('export')" title="Export">
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Scroll table -->
    <div class="scroll" ref="scroll">
      <div class="ttable" :style="{ '--tvw': tvw }">

        <!-- Group header row -->
        <div class="grp-row">
          <div class="c-mgr"></div>
          <div class="c-score"></div>
          <template v-for="(g, gi) in categoryGroups">
            <div v-if="gi > 0" :key="'gap-grp-' + g.cat.key" class="gap"></div>
            <div
              :key="'grp-' + g.cat.key"
              class="grp"
              :class="g.cat.key"
              :style="{ width: g.width + 'px' }"
            >
              <span class="lbl">
                {{ g.cat.name }}
                <button
                  v-if="g.cat.key === 'performance'"
                  class="grp-expand"
                  @click.stop="togglePerf()"
                  :title="perfExpanded ? 'Hide Goals & Culture' : 'Show Goals &amp; Culture columns'"
                >{{ perfExpanded ? '−' : '+' }}</button>
              </span>
            </div>
          </template>
        </div>

        <!-- Column header row -->
        <div class="col-row">
          <div class="c-mgr">
            <span class="mgr-h">Manager</span>
          </div>
          <div class="c-score freeze-edge">
            <span class="ch sortable" @click="toggleSort()">
              Composite <span class="arr">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </span>
          </div>
          <template v-for="(g, gi) in categoryGroups">
            <div v-if="gi > 0" :key="'gap-col-' + g.cat.key" class="gap"></div>
            <div
              v-for="m in g.cols"
              :key="'ch-' + m.key"
              class="ch"
              :style="{ width: colW(m.key) + 'px' }"
            >{{ m.head }}</div>
          </template>
        </div>

        <!-- Loading skeleton rows: mirror the real row layout (frozen manager +
             composite cells, then per-group metric boxes) so columns stay aligned
             and the table reads as loading rather than empty. -->
        <template v-if="loading">
          <div v-for="n in 7" :key="'sk-row-' + n" class="row sk-row">
            <div class="row-inner">
              <div class="c-mgr">
                <div class="mgr">
                  <span class="sk sk-avatar"></span>
                  <div class="sk-mgr-text">
                    <span class="sk sk-line sk-line-name"></span>
                    <span class="sk sk-line sk-line-team"></span>
                  </div>
                </div>
              </div>
              <div class="c-score freeze-edge">
                <span class="sk sk-chip"></span>
              </div>
              <template v-for="(g, gi) in categoryGroups">
                <div v-if="gi > 0" :key="'sk-gap-' + n + '-' + g.cat.key" class="gap"></div>
                <div
                  v-for="m in g.cols"
                  :key="'sk-cell-' + n + '-' + m.key"
                  class="cell"
                  :style="{ width: colW(m.key) + 'px' }"
                >
                  <span class="sk sk-box"></span>
                </div>
              </template>
            </div>
          </div>
        </template>

        <!-- Data rows -->
        <div
          v-for="(row, i) in pagedRows"
          :key="row.email"
          class="row"
          :class="{ expanded: expanded === row.email }"
        >
          <div class="row-inner">
            <!-- Manager frozen cell -->
            <div class="c-mgr">
              <div class="mgr" @click="toggleExpand(row.email)">
                <div
                  class="avatar"
                  :class="{ blurelement: blurMode }"
                  :style="showPhoto(row) ? null : { background: avatarColor(rowIndex(i)) }"
                >
                  <img
                    v-if="showPhoto(row)"
                    class="avatar-img"
                    :src="row.profilepic"
                    alt=""
                    @error="onAvatarError(row)"
                  />
                  <template v-else>{{ initials(row) }}</template>
                  <span
                    class="rep"
                    :class="{ crit: row.directMember >= 10, hi: row.directMember >= 7 && row.directMember < 10 }"
                  >
                    <svg width="9" height="9" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0-1a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm6 5c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4Z"/>
                    </svg>
                    {{ row.directMember }}
                  </span>
                </div>
                <div :class="{ blurelement: blurMode }">
                  <div class="nm">{{ row.fullname }}</div>
                  <div class="tm">{{ row.team_name }}</div>
                </div>
                <span class="chev-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </span>
              </div>
            </div>

            <!-- Composite score frozen cell -->
            <div class="c-score freeze-edge">
              <span class="score-chip" :class="scoreCell(row, rowIndex(i)).cls">
                <span class="v">{{ scoreCell(row, rowIndex(i)).v }}</span>
                <span class="meta">{{ scoreCell(row, rowIndex(i)).meta }}</span>
              </span>
            </div>

            <!-- Metric cells per group -->
            <template v-for="(g, gi) in categoryGroups">
              <div v-if="gi > 0" :key="'gap-row-' + row.email + '-' + g.cat.key" class="gap"></div>
              <div
                v-for="m in g.cols"
                :key="'cell-' + row.email + '-' + m.key"
                class="cell"
                :style="{ width: colW(m.key) + 'px' }"
              >
                <span class="box" :class="metricCell(row, m).cls">{{ metricCell(row, m).text }}</span>
              </div>
            </template>
          </div>
          <!-- Breakdown slot (Task 8) -->
          <div v-if="expanded === row.email && expandedBreakdown" class="breakdown">
            <div class="breakdown-inner">
              <div class="bd-title" :class="{ blurelement: blurMode }">How {{ (row.fullname || '').split(' ')[0] }}'s composite is built</div>
              <div v-if="expandedBreakdown.best" class="bd-insight">
                <template v-if="expandedBreakdown.best.cat.key === expandedBreakdown.worst.cat.key">
                  Consistent across areas, anchored by <b>{{ expandedBreakdown.best.cat.name }}</b> ({{ expandedBreakdown.best.sub }}).
                </template>
                <template v-else>
                  Strongest in <b>{{ expandedBreakdown.best.cat.name }}</b> ({{ expandedBreakdown.best.sub }}),
                  weakest in <b>{{ expandedBreakdown.worst.cat.name }}</b> ({{ expandedBreakdown.worst.sub }}).
                  <template v-if="expandedBreakdown.opp"> Biggest opportunity: <b>{{ expandedBreakdown.opp.label }}</b> at {{ expandedBreakdown.oppS }}/100.</template>
                </template>
              </div>
              <div class="bd-cats">
                <div
                  v-for="d in expandedBreakdown.cats"
                  :key="d.cat.key"
                  class="bdcat"
                  :class="{ weakest: expandedBreakdown.worst && expandedBreakdown.best && d.cat.key === expandedBreakdown.worst.cat.key && d.cat.key !== expandedBreakdown.best.cat.key }"
                >
                  <div class="bdcat-head">
                    <span class="bdcat-name">
                      <span class="bdcat-dot" :style="{ background: d.cat.color }"></span>{{ d.cat.name }}
                    </span>
                    <span class="bdcat-score" :style="{ color: subTextColor(d.sub) }">{{ d.sub == null ? '—' : d.sub }}</span>
                  </div>
                  <div class="bdcat-meta">{{ d.disabled ? 'Not scored' : catWeightFor(d.cat.key) + '% of composite' }}</div>
                  <div>
                    <div v-for="x in d.metrics" :key="x.m.key" class="bdm" :class="{ off: x.disabled }">
                      <span class="bdm-name">{{ x.m.label }}</span>
                      <span class="bdm-track">
                        <span
                          v-if="!x.disabled && x.s != null"
                          class="bdm-fill"
                          :style="{ width: x.s + '%', background: barFill(x.s) }"
                        ></span>
                      </span>
                      <span class="bdm-val">{{ x.disabled || x.s == null ? '—' : x.val }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state (filters exclude everyone) -->
        <div v-if="!loading && !pagedRows.length" class="sc-empty">
          No managers match your filters.
        </div>

      </div>
    </div>

    <!-- Pagination uses the app's house style (global .pagination + .results-status
         from main.scss): plain pager, round grey-active page numbers, status label. -->
    <div class="pagination sc-pagination" v-if="filteredRows.length > pageSize">
      <el-pagination
        layout="prev, pager, next"
        :page-size="pageSize"
        :pager-count="5"
        :current-page.sync="currentPage"
        :total="filteredRows.length"
      ></el-pagination>
      <span class="results-status">
        {{ pageStart + 1 }}–{{ pageEnd }} of {{ filteredRows.length }}
      </span>
    </div>

    <div class="foot">The composite comes from your scoring model. Performance shows only <b>Boosts on Track</b> by default. Expand it to also compare Goals and Culture.</div>

    <!-- Scoring-model settings dialog -->
    <el-dialog
      :visible.sync="modalOpen"
      width="720px"
      top="7vh"
      custom-class="scorecard-settings-dialog"
      append-to-body
      :close-on-click-modal="true"
      @closed="draft = null"
    >
      <div slot="title" class="ss-head">
        <div class="ss-title">{{ $t('insights.Scorecard_Settings') }}</div>
        <div class="ss-sub">Choose how each manager's composite score is weighted.</div>
      </div>
      <div v-if="draft">
        <!-- Performance reviews toggle -->
        <div class="ss-perf">
          <el-switch v-model="draft.includePerformance" :disabled="!performanceBoostEnabled"></el-switch>
          <div class="ss-perf-text">
            <div class="ss-perf-label">{{ $t('insights.Performance_reviews_enabled') }}</div>
            <div class="ss-perf-help">{{ performanceBoostEnabled ? 'Include review metrics (Boosts, Goals, Culture) in every score.' : 'Performance review data is not available for your workspace.' }}</div>
          </div>
        </div>

        <!-- Profile presets -->
        <div class="ss-section-label">{{ $t('insights.Profile') }}</div>
        <div class="ss-presets">
          <button v-for="p in presetKeys" :key="p" class="ss-preset" :class="{ active: draft.preset === p }" @click="applyPreset(p)">{{ presetLabel(p) }}</button>
          <button class="ss-preset ss-reset" @click="resetModel">&#8635; {{ $t('insights.Reset') }}</button>
        </div>

        <!-- Category sliders -->
        <div class="ss-section-label">{{ $t('insights.Category_Weights') }}</div>
        <div class="ss-cats">
          <div v-for="c in categories" :key="c.key" class="ss-cat" :class="{ disabled: c.key === 'performance' && !draft.includePerformance }">
            <div class="ss-cat-top">
              <span class="ss-cat-name"><span class="ss-dot" :style="{ background: c.color }"></span>{{ c.name }}</span>
              <span class="ss-cat-val">{{ (c.key === 'performance' && !draft.includePerformance) ? '—' : catPctDraft(c.key) }}<small>% of score</small></span>
            </div>
            <div class="ss-cat-metrics">{{ metricShortList(c.key) }}</div>
            <el-slider
              :value="draftCatRaw(c.key)"
              :disabled="c.key === 'performance' && !draft.includePerformance"
              :show-tooltip="false"
              @input="val => setCategory(c.key, val)"
            ></el-slider>
          </div>
        </div>

        <!-- Advanced per-metric sliders -->
        <div class="ss-adv-toggle">
          <button class="ss-link" @click="advancedOpen = !advancedOpen">{{ advancedOpen ? '▾' : '▸' }} {{ $t('insights.Advanced_weights') }}</button>
        </div>
        <div v-if="advancedOpen" class="ss-adv">
          <div v-for="c in categories" :key="c.key" class="ss-adv-col">
            <div class="ss-adv-title"><span class="ss-dot" :style="{ background: c.color }"></span>{{ c.name }}</div>
            <div v-for="m in metricsInCat(c.key)" :key="m.key" class="ss-adv-row" :class="{ dim: c.key === 'performance' && !draft.includePerformance }">
              <div class="ss-adv-rowtop"><span>{{ m.label }}</span><b>{{ (c.key === 'performance' && !draft.includePerformance) ? '—' : metricPctDraft(m.key) + '%' }}</b></div>
              <el-slider
                :value="draftMetricRaw(m.key)"
                :disabled="c.key === 'performance' && !draft.includePerformance"
                :show-tooltip="false"
                @input="val => setMetric(m.key, val)"
              ></el-slider>
            </div>
          </div>
        </div>

        <!-- Display mode -->
        <div class="ss-section-label ss-section-label--divided">{{ $t('insights.Show_composite_as') }}</div>
        <div class="ss-display-seg">
          <button v-for="d in displayModes" :key="d.key" :class="{ active: draft.displayMode === d.key }" @click="setDisplay(d.key)">{{ d.label }}</button>
        </div>
      </div>

      <div slot="footer">
        <el-button @click="cancelModel">{{ $t('insights.CANCEL') }}</el-button>
        <el-button type="primary" @click="saveModel">{{ $t('insights.Save_settings') }}</el-button>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import {
  CATEGORIES, METRICS, metricsIn, normalizeMetric, categoryScore, band, BANDS, categoryWeight, PRESET_NAMES,
  presetToWeights, DEFAULT_WEIGHTS,
} from '@/utils/managerScorecard'

const AV_COLORS = ['#6C30CC','#2f9bd6','#00b383','#e08a1e','#cc3060','#3a7abd','#7b61ff','#0ea5a5','#d4564b']

// Coerce a metric value (number, numeric string, null, or '-') to a number,
// treating missing data as 0 to match the legacy Performance Group filter.
const num = (x) => { const n = Number(x); return Number.isFinite(n) ? n : 0 }

// Named manager categories, restored from the legacy Performance Group filter so
// they stay consistent with how the rest of the product labels managers (e.g.
// "High-Performing" in Powerups). Each test runs on a row's raw metric values
// { r: reply rate, b: boosts on track, d: team DEBI, c: conversations initiated }.
// `boost` is whether performance boosts are enabled; when off, boost thresholds
// are skipped, exactly as the old filter did. Descriptions are written to read
// clearly and match the test precisely.
const MANAGER_CATEGORIES = [
  {
    key: 'elite', label: 'Elite',
    test: (m, boost) => m.r >= 80 && (!boost || m.b >= 80) && m.d >= 60,
    desc: (boost) => boost
      ? 'Replies to at least 80% of feedback, keeps at least 80% of boosts on track, and leads a team with a DEBI of 60 or higher.'
      : 'Replies to at least 80% of feedback and leads a team with a DEBI of 60 or higher.',
  },
  {
    key: 'highperf', label: 'High-Performing',
    test: (m, boost) => m.r >= 60 && (!boost || m.b >= 60) && m.d >= 50,
    desc: (boost) => boost
      ? 'Replies to at least 60% of feedback, keeps at least 60% of boosts on track, and leads a team with a DEBI of 50 or higher.'
      : 'Replies to at least 60% of feedback and leads a team with a DEBI of 50 or higher.',
  },
  {
    key: 'rising', label: 'Rising Stars',
    test: (m, boost) => m.r >= 40 && (!boost || m.b >= 40) && m.d >= 40,
    desc: (boost) => boost
      ? 'Replies to at least 40% of feedback, keeps at least 40% of boosts on track, and leads a team with a DEBI of 40 or higher.'
      : 'Replies to at least 40% of feedback and leads a team with a DEBI of 40 or higher.',
  },
  {
    key: 'fbchamp', label: 'Feedback Champions',
    test: (m) => m.c >= 60 && m.r >= 60,
    desc: () => 'Initiates at least 60% of conversations and replies to at least 60% of team feedback.',
  },
  {
    key: 'atrisk', label: 'At-Risk',
    test: (m) => m.r < 30 || m.d < 40,
    desc: () => 'Replies to under 30% of feedback, or leads a team with a DEBI below 40.',
  },
  {
    key: 'ghost', label: 'Ghost',
    test: (m, boost) => m.c === 0 && m.r === 0 && (!boost || m.b === 0),
    desc: (boost) => boost
      ? 'No activity yet: no conversations initiated, no boosts on track, and no feedback replies.'
      : 'No activity yet: no conversations initiated and no feedback replies.',
  },
]

export default {
  name: 'ManagerScorecard',
  props: {
    rows: { type: Array, default: () => [] },
    config: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    blurMode: { type: Boolean, default: false },
    performanceBoostEnabled: { type: Boolean, default: true },
    companyCreatedDate: { type: String, default: '' },
  },
  data() {
    return {
      expanded: null,
      sortDir: 'desc',
      perfExpanded: false,
      tvw: '100%',
      hasOverflow: false,
      modalOpen: false,
      draft: null,
      advancedOpen: false,
      searchText: '',
      selectedCategory: null,
      dateFilterRange: null,
      dateFilterDebounceTimer: null,
      currentPage: 1,
      pageSize: 12,
      imgErrors: {},
    }
  },
  watch: {
    searchText() { this.currentPage = 1 },
    selectedCategory() { this.currentPage = 1 },
    sortDir() { this.currentPage = 1 },
    perfExpanded() { this.currentPage = 1; this.$nextTick(this.measure) },
    rows() { this.$nextTick(this.measure) },
    dateFilterRange: {
      handler() {
        this.currentPage = 1
        this.emitDateFilter()
      },
      deep: true,
    },
    companyCreatedDate() {
      this.pruneInvalidDateFilter()
    },
  },
  computed: {
    categories() { return CATEGORIES },
    presetName() { return PRESET_NAMES[this.config.preset] || PRESET_NAMES.custom },
    effectivePerf() { return this.performanceBoostEnabled && this.config.includePerformance },
    filteredRows() {
      const q = (this.searchText || '').trim().toLowerCase()
      let rows = this.rows
      if (this.selectedCategory) {
        const cat = MANAGER_CATEGORIES.find(c => c.key === this.selectedCategory)
        const boost = !!this.performanceBoostEnabled
        if (cat) rows = rows.filter(r => cat.test(this.metricVals(r), boost))
      }
      if (q) {
        rows = rows.filter(r =>
          (r.fullname && r.fullname.toLowerCase().includes(q)) ||
          (r.team_name && r.team_name.toLowerCase().includes(q)) ||
          (r.email && r.email.toLowerCase().includes(q)))
      }
      return rows
    },
    managerCategories() {
      const boost = !!this.performanceBoostEnabled
      const counts = {}
      MANAGER_CATEGORIES.forEach(c => { counts[c.key] = 0 })
      this.rows.forEach(r => {
        const m = this.metricVals(r)
        MANAGER_CATEGORIES.forEach(c => { if (c.test(m, boost)) counts[c.key]++ })
      })
      return MANAGER_CATEGORIES.map(c => ({
        key: c.key, label: c.label, count: counts[c.key], description: c.desc(boost),
      }))
    },
    selectedCategoryConfig() {
      if (!this.selectedCategory) return null
      return this.managerCategories.find(c => c.key === this.selectedCategory) || null
    },
    sortedRows() {
      const r = [...this.filteredRows]
      r.sort((a, b) => this.sortDir === 'desc' ? b.score - a.score : a.score - b.score)
      return r
    },
    pageStart() { return (this.currentPage - 1) * this.pageSize },
    pageEnd() { return Math.min(this.pageStart + this.pageSize, this.sortedRows.length) },
    pagedRows() { return this.sortedRows.slice(this.pageStart, this.pageStart + this.pageSize) },
    avgScore() {
      if (!this.rows.length) return 0
      return Math.round(this.rows.reduce((s, r) => s + r.score, 0) / this.rows.length)
    },
    distribution() {
      const counts = {}; BANDS.forEach(b => { counts[b.key] = 0 })
      this.rows.forEach(r => { counts[band(r.score).key]++ })
      const total = this.rows.length || 1
      return BANDS.map((b, idx) => {
        const lo = b.min
        const hi = idx === 0 ? 100 : BANDS[idx - 1].min - 1
        const count = counts[b.key]
        const pct = Math.round((count / total) * 100)
        return {
          key: b.key,
          count,
          color: this.bandColor(b.key),
          label: b.label, // short label for the one-line legend
          detail: `${b.label} · ${lo}–${hi} · ${count} ${count === 1 ? 'manager' : 'managers'} (${pct}%)`,
        }
      })
    },
    barSegments() { return this.distribution.filter(d => d.count) },
    // Varying pill widths for the loading chip strip, roughly tracking the real
    // category labels (All managers / Elite / High-Performing / … / Ghost).
    chipSkeletonWidths() { return [124, 80, 142, 118, 170, 86, 78] },
    visibleMetrics() {
      return METRICS.filter(m => m.cat !== 'performance' ? true : (m.core || this.perfExpanded))
    },
    categoryGroups() {
      return CATEGORIES.map(c => {
        const cols = this.visibleMetrics.filter(m => m.cat === c.key)
        return { cat: c, cols, width: cols.reduce((s, m) => s + this.colW(m.key), 0) }
      })
    },
    expandedBreakdown() {
      if (!this.expanded) return null
      const row = this.sortedRows.find(r => r.email === this.expanded)
      if (!row) return null
      return this.breakdownFor(row)
    },
    presetKeys() { return ['balanced', 'engagement', 'performance', 'people'] },
    displayModes() {
      return [
        { key: 'score', label: '0–100 score' },
        { key: 'grade', label: 'Letter grade' },
        { key: 'percentile', label: 'Rank' },
      ]
    },
    datePickerOptions() {
      const minDate = this.startOfDay(this.coerceDate(this.companyCreatedDate))
      const today = this.startOfDay(new Date())

      return {
        disabledDate: (date) => {
          const day = this.startOfDay(date)
          return (minDate && day < minDate) || day > today
        },
      }
    },
    hasDateFilter() {
      return Array.isArray(this.dateFilterRange) &&
        !!this.dateFilterRange[0] &&
        !!this.dateFilterRange[1]
    },
    dateLabel() {
      if (!this.hasDateFilter) return 'Past 30 days'
      const a = this.coerceDate(this.dateFilterRange[0])
      const b = this.coerceDate(this.dateFilterRange[1])
      if (!a || !b) return 'Past 30 days'
      const sameYear = a.getFullYear() === b.getFullYear()
      const fmt = (d, withYear) => d.toLocaleDateString('en-US',
        withYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' })
      return `${fmt(a, !sameYear)} – ${fmt(b, true)}`
    },
  },
  methods: {
    // ---- Settings modal ----
    openSettings() {
      this.draft = JSON.parse(JSON.stringify(this.config))
      if (!this.performanceBoostEnabled) this.draft.includePerformance = false
      this.modalOpen = true
    },
    applyPreset(key) { this.draft.preset = key; this.draft.weights = presetToWeights(key) },
    // Reset restores the default model (Insights Opened at 0), not an even split.
    resetModel() { this.draft.preset = 'custom'; this.draft.weights = { ...DEFAULT_WEIGHTS } },
    setCategory(catKey, val) {
      // Independent: proportionally scale this category's metrics to a raw total of `val`,
      // leaving other categories untouched. Effective %s are derived (composite re-normalizes).
      const w = { ...this.draft.weights }
      const ms = metricsIn(catKey)
      const curr = ms.reduce((s, m) => s + (w[m.key] || 0), 0)
      if (curr > 0) ms.forEach(m => { w[m.key] = (w[m.key] || 0) / curr * Number(val) })
      else ms.forEach(m => { w[m.key] = Number(val) / ms.length })
      this.draft.weights = w
      this.draft.preset = 'custom'
    },
    setMetric(key, val) {
      // Independent: set this metric's raw weight only — nothing else moves, so it can reach 0.
      this.draft.weights = { ...this.draft.weights, [key]: Number(val) }
      this.draft.preset = 'custom'
    },
    setDisplay(mode) { this.draft.displayMode = mode },
    totalActiveRaw(weights, includePerf) {
      let t = 0
      METRICS.forEach(m => { if (m.cat === 'performance' && !includePerf) return; t += (weights[m.key] || 0) })
      return t
    },
    metricPct(weights, key, includePerf) {
      const m = METRICS.find(x => x.key === key)
      if (m && m.cat === 'performance' && !includePerf) return 0
      const t = this.totalActiveRaw(weights, includePerf)
      return t ? Math.round((weights[key] || 0) / t * 100) : 0
    },
    catPct(weights, catKey, includePerf) {
      if (catKey === 'performance' && !includePerf) return 0
      const t = this.totalActiveRaw(weights, includePerf)
      if (!t) return 0
      const raw = metricsIn(catKey).reduce((s, m) => s + (weights[m.key] || 0), 0)
      return Math.round(raw / t * 100)
    },
    catPctDraft(catKey) { return this.catPct(this.draft.weights, catKey, this.draft.includePerformance) },
    metricPctDraft(key) { return this.metricPct(this.draft.weights, key, this.draft.includePerformance) },
    draftCatRaw(catKey) { return Math.round(categoryWeight(this.draft.weights, catKey)) },
    draftMetricRaw(key) { return Math.round(this.draft.weights[key] || 0) },
    saveModel() {
      // Normalize raw weights to sum 100 (preserves ratios + zeros) so the saved model stays tidy.
      const w = { ...this.draft.weights }
      const total = METRICS.reduce((s, m) => s + (w[m.key] || 0), 0)
      if (total > 0) METRICS.forEach(m => { w[m.key] = (w[m.key] || 0) / total * 100 })
      this.draft.weights = w
      this.$emit('save', JSON.parse(JSON.stringify(this.draft)))
      this.modalOpen = false
    },
    cancelModel() { this.modalOpen = false },
    presetLabel(p) { return PRESET_NAMES[p] },
    metricsInCat(k) { return metricsIn(k) },
    metricShortList(k) { return metricsIn(k).map(m => m.head).join(' · ') },

    // ---- Breakdown helpers (Task 8) ----
    breakdownFor(row) {
      const cats = CATEGORIES.map(c => {
        const disabled = c.key === 'performance' && !row.includePerformance
        const sub = disabled ? null : this.catScore(row, c.key)
        const metrics = metricsIn(c.key).map(m => ({
          m,
          disabled,
          s: disabled ? null : normalizeMetric(m.key, row.values[m.key]),
          val: m.fmt === 'count' ? row.values[m.key] : (m.fmt === 'num' ? row.values[m.key] : row.values[m.key] + '%'),
        }))
        return { cat: c, sub, disabled, metrics }
      })
      const active = cats.filter(c => !c.disabled && c.sub != null)
      const best = active.length ? active.reduce((a, b) => b.sub > a.sub ? b : a) : null
      const worst = active.length ? active.reduce((a, b) => b.sub < a.sub ? b : a) : null
      let opp = null; let oppS = 101
      cats.forEach(c => c.metrics.forEach(x => {
        if (!x.disabled && x.s != null && x.s < oppS) { oppS = x.s; opp = x.m }
      }))
      return { cats, best, worst, opp, oppS: Math.round(oppS) }
    },
    barFill(s) { return s >= 70 ? '#76ca7d' : s >= 45 ? '#eed24e' : '#ff4e4e' },
    subTextColor(s) { return s == null ? '#9aa0ad' : s >= 70 ? '#1f9d6b' : s >= 55 ? '#b8860b' : s >= 40 ? '#e0721e' : '#cc3030' },
    // ---- end breakdown helpers ----
    bandColor(key) {
      return { promoter: '#00b383', high: '#76ca7d', moderate: '#eed24e', low: '#ff4e4e', zero: '#d33e3e' }[key]
    },
    catWeightFor(catKey) {
      return this.catPct(this.config.weights, catKey, this.effectivePerf)
    },
    colW(key) {
      return ({ conv_initiated: 76, peer_feedback: 72, reviews_completed: 80, goals_progress: 74, culture_contribution: 80, reply_rate: 74, insights_opened: 84, debi: 70 })[key]
    },
    metricCell(row, m) {
      if (m.cat === 'performance' && !row.includePerformance) return { cls: 'box-gray', text: '—' }
      const s = normalizeMetric(m.key, row.values[m.key])
      const raw = row.values[m.key]
      const text = m.fmt === 'count' ? raw : (m.fmt === 'num' ? raw : raw + '%')
      return { cls: s == null ? 'box-gray' : s >= 70 ? 'box-green' : s >= 45 ? 'box-yellow' : 'box-red', text: s == null ? '—' : text }
    },
    catScore(row, catKey) {
      return categoryScore(row.values, catKey, this.config.weights, { includePerformance: row.includePerformance })
    },
    scoreCell(row, idx) {
      const b = band(row.score)
      if (this.config.displayMode === 'grade') return { cls: 'sc-' + b.key, v: b.grade, meta: b.label }
      if (this.config.displayMode === 'percentile') {
        const top = Math.max(1, Math.round(((idx + 1) / this.sortedRows.length) * 100))
        return { cls: 'sc-' + b.key, v: '#' + (idx + 1), meta: 'Top ' + top + '%' }
      }
      return { cls: 'sc-' + b.key, v: Math.round(row.score), meta: b.label }
    },
    initials(row) {
      return row.shortname || (row.fullname || '').split(' ').map(s => s[0]).slice(0, 2).join('')
    },
    // Show the profile photo when one is available and hasn't failed to load;
    // otherwise fall back to the coloured initials avatar.
    showPhoto(row) { return !!row.profilepic && !this.imgErrors[row.email] },
    onAvatarError(row) { this.$set(this.imgErrors, row.email, true) },
    avatarColor(idx) {
      return AV_COLORS[idx % AV_COLORS.length]
    },
    rowIndex(i) { return this.pageStart + i },
    metricVals(row) {
      const v = row.values || {}
      return { r: num(v.reply_rate), b: num(v.reviews_completed), d: num(v.debi), c: num(v.conv_initiated) }
    },
    coerceDate(value) {
      if (!value) return null
      if (value instanceof Date) return isNaN(value.getTime()) ? null : value
      if (typeof value === 'string') {
        const dateOnly = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (dateOnly) {
          return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        }
      }

      const date = new Date(value)
      return isNaN(date.getTime()) ? null : date
    },
    startOfDay(date) {
      if (!date) return null
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    },
    isDateOutOfBounds(value) {
      const date = this.startOfDay(this.coerceDate(value))
      if (!date) return false

      const minDate = this.startOfDay(this.coerceDate(this.companyCreatedDate))
      const today = this.startOfDay(new Date())
      return (minDate && date < minDate) || date > today
    },
    pruneInvalidDateFilter() {
      if (Array.isArray(this.dateFilterRange) && this.dateFilterRange.some(date => this.isDateOutOfBounds(date))) {
        this.dateFilterRange = null
      }
    },
    buildDateFilterPayload() {
      const range = Array.isArray(this.dateFilterRange) ? this.dateFilterRange : []
      return {
        mode: 'range',
        day: '',
        startdate: range[0] || '',
        enddate: range[1] || '',
      }
    },
    emitDateFilter() {
      if (this.dateFilterDebounceTimer) {
        clearTimeout(this.dateFilterDebounceTimer)
      }
      this.dateFilterDebounceTimer = setTimeout(() => {
        this.dateFilterDebounceTimer = null
        this.$emit('date-filter-change', this.buildDateFilterPayload())
      }, 200)
    },
    onDateCommand(cmd) {
      if (cmd === 'custom') this.openCustomRange()
      else this.selectDefaultRange()
    },
    selectDefaultRange() {
      // null range -> emits an empty payload -> parent reverts to the base
      // rolling-30-day data (no fetch).
      this.dateFilterRange = null
    },
    openCustomRange() {
      // Let the dropdown finish closing, then focus the hidden range input so
      // Element opens its calendar panel anchored under the pill.
      setTimeout(() => {
        const picker = this.$refs.rangePicker
        if (picker && typeof picker.focus === 'function') picker.focus()
      }, 60)
    },
    toggleCategory(key) { this.selectedCategory = this.selectedCategory === key ? null : key },
    toggleExpand(email) { this.expanded = this.expanded === email ? null : email },
    toggleSort() { this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc' },
    togglePerf() { this.perfExpanded = !this.perfExpanded },
    measure() {
      const s = this.$refs.scroll
      if (!s) return
      this.tvw = (s.clientWidth - 4) + 'px'
      // Only flag overflow (and so the freeze-edge shadow) when columns don't all fit.
      this.hasOverflow = (s.scrollWidth - s.clientWidth) > 1
    },
  },
  mounted() { this.measure(); window.addEventListener('resize', this.measure) },
  beforeDestroy() {
    if (this.dateFilterDebounceTimer) clearTimeout(this.dateFilterDebounceTimer)
    window.removeEventListener('resize', this.measure)
  },
}
</script>

<style scoped>
.manager-scorecard {
  --mgrW: 236px;
  --scoreW: 148px;
  --freezeW: 384px;
  --primary: #6C30CC;
  --primary-hover: #522698;
  --primary-tint: #f2ecfb;
  --page: #f5f5fa;
  --muted: #fafbfd;
  --divider: #eef0f5;
  --border: #e2e4ea;
  --ink: #1a1a2e;
  --ink-soft: #4a4a4a;
  --ink-mute: #8a8a8a;
  /* Metric-cell tints share the chip pastels below, so "good / ok / bad" reads
     as one palette across the composite chips and the per-metric cells. */
  --box-green: #D9FDD8;
  --box-yellow: #FFF4D6;
  --box-red: #FFEAEA;
  --box-gray: #e7e7e7;
  --band-promoter: #00b383;
  --band-high: #76ca7d;
  --band-moderate: #eed24e;
  --band-low: #ff4e4e;
  --band-zero: #d33e3e;
  --chip-promoter-bg: #C2F5CB;
  --chip-promoter-fg: #075e48;
  --chip-high-bg: #D9FDD8;
  --chip-high-fg: #2d5e33;
  --chip-moderate-bg: #FFF4D6;
  --chip-moderate-fg: #7a5d00;
  --chip-low-bg: #FFEAEA;
  --chip-low-fg: #991b1b;
  --chip-zero-bg: #F7CFD0;
  --chip-zero-fg: #7f1d1d;
  font-family: Lato, Arial, sans-serif;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
}

/* ---- sec-head ---- */
.sec-head { display: flex; align-items: flex-start; gap: 20px; }
.sec-head .t { font-size: 18px; font-weight: 700; }
.sec-head .d { font-size: 13px; color: #747474; margin-top: 6px; max-width: 660px; line-height: 1.55; }
.sec-head .spacer { margin-left: auto; }

/* ---- model-chip ---- */
.model-chip { display: inline-flex; align-items: center; gap: 12px; background: #fff; border: 1px solid var(--border); border-radius: 8px; padding: 9px 12px 9px 14px; cursor: pointer; font-family: inherit; transition: all .14s ease; box-shadow: 0 1px 2px rgba(17,24,39,.03); white-space: nowrap; }
.model-chip:hover { border-color: #c9b8ee; box-shadow: 0 2px 8px rgba(108,48,204,.1); }
.model-chip .mc-label { font-size: 10px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-mute); }
.model-chip .mc-name { font-size: 13.5px; font-weight: 800; color: var(--ink); }
.model-chip .mc-bar { display: flex; width: 70px; height: 7px; border-radius: 999px; overflow: hidden; }
.model-chip .mc-bar span { display: block; transition: flex-grow .3s ease; }
.model-chip .mc-go { display: inline-flex; align-items: center; gap: 5px; color: var(--primary); font-size: 12.5px; font-weight: 700; padding-left: 10px; border-left: 1px solid var(--divider); }

/* ---- card / dist ---- */
.card { background: #fff; border: 1px solid var(--divider); border-radius: 12px; box-shadow: 0 1px 2px rgba(17,24,39,.04), 0 8px 24px rgba(17,24,39,.04); }
.dist { padding: 13px 20px; display: flex; align-items: center; gap: 22px; margin-top: 18px; }
.dist-avg { display: flex; align-items: baseline; gap: 9px; padding-right: 22px; border-right: 1px solid var(--divider); }
.dist-avg .n { font-size: 30px; font-weight: 900; letter-spacing: -.02em; font-variant-numeric: tabular-nums; }
.dist-avg .lbl { font-size: 10.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-mute); line-height: 1.2; }
.dist-bar-wrap { flex: 1; min-width: 0; }
.dist-bar { display: flex; height: 11px; border-radius: 999px; overflow: hidden; background: var(--divider); }
.dist-bar .seg { display: block; cursor: default; transition: flex-grow .4s cubic-bezier(.4,0,.2,1); }
.dist-legend { display: flex; gap: 7px 16px; margin-top: 10px; flex-wrap: wrap; }
.dist-legend .item { font-size: 11.5px; color: var(--ink-soft); display: flex; align-items: center; gap: 6px; font-weight: 600; cursor: default; white-space: nowrap; }
.dist-legend .sw { width: 9px; height: 9px; border-radius: 999px; flex: none; }
.dist-legend .item b { font-weight: 900; }

/* ---- loading skeleton ----
   One shimmer primitive (.sk) reused across the avg block, legend counts, chips,
   toolbar count, and table rows. A single sheen sweep keeps the motion calm and
   coherent rather than a field of independently-pulsing blocks. */
.sk { position: relative; overflow: hidden; display: inline-block; background: #e9ebf1; border-radius: 6px; }
.sk::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent 0, rgba(255, 255, 255, .65) 50%, transparent 100%);
  animation: sc-sheen 1.5s ease-in-out infinite;
}
@keyframes sc-sheen { 100% { transform: translateX(100%); } }

/* distribution card */
.dist.is-loading .dist-avg { align-items: center; }
.sk-avg { width: 46px; height: 30px; border-radius: 8px; }
/* Preview the bar's structure: five equal faint band segments under one sheen
   sweep, so the shape that's coming is implied without faking any numbers. */
.dist-bar.sk-bar { position: relative; }
.dist-bar.sk-bar .sk-seg { flex: 1 1 0; opacity: .4; transition: none; }
.dist-bar.sk-bar::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent 0, rgba(255, 255, 255, .55) 50%, transparent 100%);
  animation: sc-sheen 1.6s ease-in-out infinite;
}
.dist.is-loading .dist-legend .item { color: var(--ink-mute); }
.sk-count { width: 18px; height: 11px; border-radius: 4px; }
.sk-count-lg { width: 84px; height: 14px; border-radius: 6px; }

/* category chips */
.mg-chip-sk { height: 32px; border-radius: 999px; }

/* table rows */
.sk-row { pointer-events: none; }
.sk-row:hover { box-shadow: 0 0 6px #0000001f; }
.sk-avatar { width: 36px; height: 36px; border-radius: 50%; flex: none; }
.sk-mgr-text { display: flex; flex-direction: column; gap: 7px; }
.sk-line { height: 11px; border-radius: 4px; }
.sk-line-name { width: 108px; }
.sk-line-team { width: 74px; height: 9px; }
.sk-chip { width: 96px; height: 30px; border-radius: 8px; }
.sk-box { width: 50px; height: 28px; border-radius: 8px; }

/* ---- manager-category filter chips (mirrors people.vue talent-group strip) ---- */
.mg-strip { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 16px; }
.mg-chip { display: inline-flex; align-items: center; gap: 8px; height: 32px; padding: 0 12px; border-radius: 999px; background: #fff; border: 1px solid var(--border); color: var(--ink-soft); font-size: 13px; font-weight: 600; line-height: 1; cursor: pointer; font-family: inherit; white-space: nowrap; transition: background .15s ease, border-color .15s ease, color .15s ease; }
.mg-chip:hover { background: #fafbfc; border-color: #d4d7de; color: #2a2a2a; }
.mg-chip:focus-visible { outline: 2px solid rgba(108,48,204,.35); outline-offset: 1px; }
.mg-chip.active { background: rgba(108,48,204,.10); border-color: rgba(108,48,204,.30); color: #4C32CC; }
.mg-chip.active .mg-chip-count { background: rgba(108,48,204,.18); color: #4C32CC; }
.mg-chip.empty:not(.active) { color: #b3b3b3; border-color: var(--divider); }
.mg-chip.empty:not(.active) .mg-chip-count { background: #f3f4f8; color: #b3b3b3; }
.mg-chip-label { letter-spacing: .01em; }
.mg-chip-count { display: inline-flex; align-items: center; justify-content: center; min-width: 22px; height: 18px; padding: 0 6px; border-radius: 999px; background: #f3f4f8; color: #80808a; font-size: 11px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; transition: background .15s ease, color .15s ease; }

.mg-criteria { display: flex; align-items: flex-start; background: #f2edfa; border: 1px solid rgba(108,48,204,.18); border-radius: 8px; padding: 11px 14px; margin-top: 12px; }
.mg-criteria-icon { color: var(--primary); font-size: 17px; margin-right: 11px; margin-top: 1px; flex: none; }
.mg-criteria-details { flex: 1; }
.mg-criteria-title { font-weight: 800; font-size: 13.5px; color: var(--ink); }
.mg-criteria-desc { font-size: 12.5px; color: #666; line-height: 1.5; margin-top: 1px; }
.mg-criteria-close { background: none; border: none; color: #999; cursor: pointer; font-size: 15px; padding: 0; margin-left: 10px; flex: none; line-height: 1; }
.mg-criteria-close:hover { color: #333; }

.sc-empty { padding: 34px 0; text-align: center; color: var(--ink-mute); font-size: 13px; font-weight: 600; }

/* ---- toolbar ---- */
.toolbar { margin: 22px 2px 4px; }
.toolbar-top { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 14px; width: 100%; }
.toolbar .sc-search { max-width: 300px; }
.toolbar .count { font-size: 13px; font-weight: 700; color: var(--ink-mute); white-space: nowrap; }
.toolbar-actions { display: inline-flex; align-items: center; justify-content: flex-end; gap: 14px; margin-left: auto; flex: none; min-width: 0; }
.toolbar .sc-export { width: 36px; height: 36px; border-radius: 8px; border: 1px solid var(--border); background: #fff; color: #6a6a6a; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; flex: none; transition: all .14s ease; }
.toolbar .sc-export:hover { border-color: var(--primary); color: var(--primary); background: var(--primary-tint); }

/* table-local date filter — preset pill dropdown + on-demand range picker */
.date-control { position: relative; display: inline-flex; flex: none; max-width: 100%; }
.date-control.is-disabled { opacity: .62; pointer-events: none; }
/* pill trigger (extends the global .header-pill-dropdown standard) */
.date-pill { height: 40px; gap: 7px; }
.date-pill-icon { font-size: 15px; color: var(--ink-mute); }
.date-pill.active .date-pill-icon { color: var(--primary); }
.date-pill-label { font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
.date-pill .el-icon-arrow-down { margin-left: 4px; font-size: 12px; }
.date-pill-clear { width: 20px; height: 20px; margin-left: 2px; border: none; border-radius: 5px; background: transparent; color: currentColor; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; flex: none; font-size: 13px; transition: background .14s ease, color .14s ease; }
.date-pill-clear:hover { background: #fff0f0; color: #cc3030; }
/* the real range input is hidden but kept on top of the pill so Element anchors
   its calendar popper directly under the pill when we focus() it. */
.df-hidden-picker { position: absolute; inset: 0; width: 100%; opacity: 0; pointer-events: none; }

@media (max-width: 1020px) {
  .toolbar .sc-search { flex: 1 1 240px; max-width: none; }
  .toolbar-actions { gap: 10px; margin-left: auto; }
}

@media (max-width: 560px) {
  .toolbar-actions { width: auto; }
  .date-control { flex: 1 1 200px; }
  .date-pill { width: 100%; }
}
/* Layout only; pager + .results-status styling come from the global .pagination
   block in main.scss, so this matches every other paginated list in the app. */
.sc-pagination { display: flex; align-items: center; gap: 14px; padding: 16px 0 2px; }

/* ---- frozen-column scroll table ---- */
.scroll { overflow-x: auto; padding: 0 2px 6px; margin-top: 4px; }
.scroll::-webkit-scrollbar { height: 9px; }
.scroll::-webkit-scrollbar-thumb { background: #d3d3de; border-radius: 6px; }
.ttable { min-width: max-content; }

.grp-row, .col-row, .row-inner { display: flex; align-items: stretch; }

/* sticky freeze cells */
/* border-box so the header's padding-left (.col-row .c-mgr) stays inside the
   fixed column width instead of widening it 14px and shifting every metric
   header right of its data cell. */
.c-mgr { width: var(--mgrW); flex: none; position: sticky; left: 0; z-index: 3; box-sizing: border-box; }
.c-score { width: var(--scoreW); flex: none; position: sticky; left: var(--mgrW); z-index: 3; box-sizing: border-box; display: flex; align-items: center; justify-content: center; }
/* Freeze-edge shadow is a scroll affordance — only show it when columns
   actually overflow the viewport, otherwise it's just clutter. */
.freeze-edge { box-shadow: none; transition: box-shadow .2s ease; }
.manager-scorecard.has-overflow .freeze-edge { box-shadow: 10px 0 12px -10px rgba(17,24,39,.16); }

/* group title row */
.grp-row { margin-bottom: 2px; }
/* Frozen header cells must be opaque to mask horizontally-scrolling columns,
   but they sit on the white surface (like the data rows) — so they're white,
   not page-tinted, otherwise the Manager+Composite block reads as a stray box. */
.grp-row .c-mgr, .grp-row .c-score { background: #fff; }
.grp { display: flex; align-items: flex-end; justify-content: center; padding-bottom: 7px; overflow: visible; }
.grp .lbl { font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: var(--ink-mute); position: relative; padding-bottom: 6px; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.grp .lbl::after { content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 2px; border-radius: 2px; opacity: .5; }
.grp.leadership .lbl::after { background: #6C30CC; }
.grp.performance .lbl::after { background: #2f9bd6; }
.grp.engagement .lbl::after { background: #00b383; }
.grp-expand { border: 1px solid var(--border); background: #fff; color: var(--primary); border-radius: 50%; width: 18px; height: 18px; padding: 0; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; line-height: 1; display: inline-flex; align-items: center; justify-content: center; flex: none; margin-bottom: 0; }
.grp-expand:hover { background: var(--primary-tint); border-color: var(--primary); }
.gap { width: 18px; flex: none; }

/* column header row */
.col-row { padding-bottom: 8px; border-bottom: 1px solid var(--divider); }
.col-row .c-mgr, .col-row .c-score { background: #fff; display: flex; align-items: flex-end; }
.col-row .c-mgr { padding-left: 14px; }
.ch { font-size: 11px; font-weight: 700; color: #6b7280; letter-spacing: .01em; display: flex; align-items: flex-end; justify-content: center; flex: none; padding-bottom: 0; }
.ch.sortable { cursor: pointer; gap: 4px; }
.ch.sortable .arr { color: var(--primary); font-size: 10px; }
.mgr-h { font-size: 11px; font-weight: 700; color: #6b7280; letter-spacing: .01em; }

/* data rows */
.row { background: #fff; border-radius: 8px; box-shadow: 0 0 6px #0000001f; min-height: 60px; margin-top: 12px; transition: box-shadow .15s ease; }
.row .row-inner { min-height: 60px; }
.row:hover { box-shadow: 0 2px 12px #00000026; }
.row.expanded { box-shadow: 0 2px 14px #6c30cc26; }
.row .c-mgr, .row .c-score { background: #fff; }
.row .c-mgr { border-radius: 8px 0 0 8px; }

.cell { flex: none; display: flex; align-items: center; justify-content: center; }
.mgr { display: flex; align-items: center; gap: 11px; cursor: pointer; padding: 12px 0 12px 14px; width: 100%; }
.avatar { width: 36px; height: 36px; border-radius: 50%; flex: none; display: grid; place-items: center; color: #fff; font-size: 13px; font-weight: 800; position: relative; }
.avatar-img { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; display: block; }
.avatar .rep { position: absolute; bottom: -3px; right: -4px; background: #fff; color: #5a5a6a; border: 1px solid var(--border); border-radius: 999px; font-size: 9px; font-weight: 800; padding: 0 4px; height: 15px; display: flex; align-items: center; gap: 2px; }
.avatar .rep.hi { color: #b45309; border-color: #fcd9a0; }
.avatar .rep.crit { color: #b42318; border-color: #f6b9b2; }
.mgr .nm { font-size: 14px; font-weight: 700; color: #3c3c3c; line-height: 1.2; }
.mgr .tm { font-size: 13px; color: #8a8a8a; font-weight: 500; }
.chev-btn { margin-left: auto; margin-right: 6px; color: #c2c2cc; transition: transform .18s ease; }
.row.expanded .chev-btn { transform: rotate(90deg); color: var(--primary); }

/* score chip */
.score-chip { min-width: 96px; display: inline-flex; align-items: center; gap: 8px; justify-content: center; padding: 7px 12px; border-radius: 8px; font-weight: 900; font-variant-numeric: tabular-nums; transition: background .25s ease, color .25s ease; }
.score-chip .v { font-size: 16px; line-height: 1; letter-spacing: -.01em; }
/* Band label: same eyebrow family as the AVG summary label and group labels
   (.dist-avg .lbl / .grp .lbl) — crisp (no fade), matched weight + tracking.
   nowrap keeps two-word bands (Role Model / Needs Coaching) on one line. */
.score-chip .meta { font-size: 9.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; text-align: left; line-height: 1.1; white-space: nowrap; }
.sc-promoter { background: var(--chip-promoter-bg); color: var(--chip-promoter-fg); }
.sc-high { background: var(--chip-high-bg); color: var(--chip-high-fg); }
.sc-moderate { background: var(--chip-moderate-bg); color: var(--chip-moderate-fg); }
.sc-low { background: var(--chip-low-bg); color: var(--chip-low-fg); }
.sc-zero { background: var(--chip-zero-bg); color: var(--chip-zero-fg); }

/* metric boxes */
.box { border-radius: 8px; width: 50px; height: 28px; display: inline-flex; justify-content: center; align-items: center; color: #000; font-size: 11.5px; font-weight: 700; font-variant-numeric: tabular-nums; }
.box-green { background: var(--box-green); }
.box-yellow { background: var(--box-yellow); }
.box-red { background: var(--box-red); }
.box-gray { background: var(--box-gray); color: #9aa0ad; }

/* ---- breakdown panel (Task 8) ---- */
/* sticky: pins to viewport left-edge while table scrolls horizontally */
.breakdown { position: sticky; left: 0; width: var(--tvw); padding: 0 14px 16px; }
.breakdown-inner { background: #faf8fe; border: 1px solid #efe9fb; border-radius: 8px; padding: 16px 18px; }
.bd-title { font-size: 11px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-mute); }
.bd-insight { font-size: 13.5px; color: var(--ink-soft); margin-top: 5px; line-height: 1.5; }
.bd-insight b { font-weight: 800; color: var(--ink); }
.bd-cats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 15px; }
.bdcat { background: #fff; border: 1px solid var(--divider); border-radius: 8px; padding: 13px 15px 9px; }
.bdcat.weakest { border-color: #f3c7c7; box-shadow: 0 0 0 1px #fbe3e3 inset; }
.bdcat-head { display: flex; align-items: center; justify-content: space-between; }
.bdcat-name { font-size: 12px; font-weight: 800; display: flex; align-items: center; gap: 7px; }
.bdcat-dot { width: 8px; height: 8px; border-radius: 3px; flex: none; }
.bdcat-score { font-size: 21px; font-weight: 900; font-variant-numeric: tabular-nums; line-height: 1; }
.bdcat-meta { font-size: 11px; color: var(--ink-mute); font-weight: 700; margin-top: 2px; margin-bottom: 11px; }
.bdm { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; }
.bdm:last-child { margin-bottom: 2px; }
.bdm-name { font-size: 11.5px; color: #54545f; font-weight: 600; width: 98px; flex: none; line-height: 1.2; }
.bdm-track { flex: 1; height: 7px; border-radius: 999px; background: #eef0f5; overflow: hidden; }
.bdm-fill { display: block; height: 100%; border-radius: 999px; transition: width .35s cubic-bezier(.4,0,.2,1); }
.bdm-val { font-size: 11.5px; font-weight: 800; width: 36px; text-align: right; flex: none; font-variant-numeric: tabular-nums; color: var(--ink); }
.bdm.off { opacity: .45; }

/* foot */
.foot { padding: 16px 4px 0; font-size: 12px; color: #8a8a8a; }

/* ---- Settings modal (custom content; Element chrome themed in the global block below) ---- */
.ss-head { padding-right: 26px; }
.ss-title { font-size: 18px; font-weight: 700; color: var(--ink); letter-spacing: -.01em; }
.ss-sub { font-size: 12.5px; color: var(--ink-mute); margin-top: 4px; font-weight: 500; }

.ss-perf { display: flex; align-items: center; gap: 12px; background: var(--muted); border: 1px solid var(--divider); border-radius: 8px; padding: 12px 14px; }
.ss-perf-label { font-size: 13px; font-weight: 700; color: var(--ink); }
.ss-perf-help { font-size: 11.5px; color: var(--ink-mute); margin-top: 1px; }

.ss-section-label { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--ink-mute); margin: 22px 0 12px; }
.ss-section-label--divided { margin-top: 24px; padding-top: 22px; border-top: 1px solid var(--divider); }

.ss-presets { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.ss-preset { border: 1px solid var(--border); background: #fff; color: var(--ink-soft); border-radius: 999px; padding: 8px 16px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all .14s ease; display: inline-flex; align-items: center; gap: 7px; }
.ss-preset:hover { border-color: #c9b8ee; background: #faf8fe; color: var(--primary-hover); }
.ss-preset.active { background: var(--primary-tint); border-color: var(--primary); color: var(--primary-hover); }
.ss-reset { margin-left: auto; border-style: dashed; color: var(--ink-mute); font-weight: 600; }
.ss-reset:hover { color: var(--ink-soft); background: #fafbfd; border-color: var(--border); }

.ss-cats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.ss-cat { border: 1px solid var(--divider); border-radius: 8px; padding: 14px 16px 16px; background: var(--muted); transition: opacity .18s ease; }
.ss-cat.disabled { opacity: .45; }
.ss-cat-top { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.ss-cat-name { font-size: 13px; font-weight: 800; display: flex; align-items: center; gap: 7px; color: var(--ink); }
.ss-dot { width: 9px; height: 9px; border-radius: 999px; flex: none; display: inline-block; }
.ss-cat-val { font-size: 18px; font-weight: 900; font-variant-numeric: tabular-nums; color: var(--ink); white-space: nowrap; }
.ss-cat-val small { font-size: 10.5px; font-weight: 700; color: var(--ink-mute); margin-left: 1px; }
.ss-cat-metrics { font-size: 11px; color: var(--ink-mute); margin-top: 3px; font-weight: 600; }

.ss-adv-toggle { margin-top: 14px; }
.ss-link { background: none; border: none; color: var(--primary); font-family: inherit; font-size: 12.5px; font-weight: 700; cursor: pointer; padding: 0; display: inline-flex; align-items: center; gap: 5px; }
.ss-link:hover { color: var(--primary-hover); }
.ss-adv { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 14px; border-top: 1px dashed var(--border); padding-top: 16px; }
.ss-adv-title { font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; color: var(--ink-soft); }
.ss-adv-row { margin-bottom: 12px; }
.ss-adv-row.dim { opacity: .4; }
.ss-adv-rowtop { display: flex; justify-content: space-between; font-size: 12px; font-weight: 600; color: var(--ink-soft); margin-bottom: 3px; }
.ss-adv-rowtop b { font-weight: 900; color: var(--ink); font-variant-numeric: tabular-nums; }

.ss-display-seg { display: inline-flex; background: var(--page); border: 1px solid var(--divider); border-radius: 8px; padding: 3px; gap: 2px; }
.ss-display-seg button { font-family: inherit; border: none; background: none; font-size: 12.5px; font-weight: 700; color: var(--ink-mute); padding: 7px 16px; border-radius: 6px; cursor: pointer; transition: all .14s ease; }
.ss-display-seg button:hover { color: var(--primary-hover); }
.ss-display-seg button.active { background: var(--primary-tint); color: var(--primary-hover); }
</style>

<!-- Dialog chrome + branded Element controls. Not scoped: the dialog is teleported
     to <body> (append-to-body), so component-scoped rules and CSS vars can't reach
     Element's internals. Keyed to the custom-class so no other dialog is affected. -->
<style>
/* The component's design tokens live on .manager-scorecard, but this dialog is
   teleported to <body>, so those vars don't cascade in and every scoped ss-*
   rule that uses var(--*) (presets, the "Show composite as" segment, category
   cards) renders unstyled — including the active-state highlight. Re-publish the
   tokens on the dialog root so the scoped rules resolve. */
.scorecard-settings-dialog {
  --primary: #6C30CC; --primary-hover: #522698; --primary-tint: #f2ecfb;
  --page: #f5f5fa; --muted: #fafbfd; --divider: #eef0f5; --border: #e2e4ea;
  --ink: #1a1a2e; --ink-soft: #4a4a4a; --ink-mute: #8a8a8a;
}
.scorecard-settings-dialog .el-dialog__header { padding: 24px 28px 4px; }
.scorecard-settings-dialog .el-dialog__body { padding: 12px 28px 20px; color: #1a1a2e; }
.scorecard-settings-dialog .el-dialog__footer { padding: 4px 28px 22px; }
.scorecard-settings-dialog .el-dialog__headerbtn { top: 26px; right: 24px; }
.scorecard-settings-dialog .el-dialog__headerbtn .el-dialog__close { font-size: 18px; color: #9a9aa3; transition: color .14s ease; }
.scorecard-settings-dialog .el-dialog__headerbtn:hover .el-dialog__close { color: #6C30CC; }

/* sliders + switch in the brand purple, to match the scorecard (Element defaults to blue) */
.scorecard-settings-dialog .el-slider:not(.is-disabled) .el-slider__bar { background-color: #6C30CC; }
.scorecard-settings-dialog .el-slider:not(.is-disabled) .el-slider__button { border-color: #6C30CC; }
.scorecard-settings-dialog .el-switch.is-checked .el-switch__core { background-color: #6C30CC; border-color: #6C30CC; }

/* primary action in the brand purple */
.scorecard-settings-dialog .el-button--primary { background-color: #6C30CC; border-color: #6C30CC; }
.scorecard-settings-dialog .el-button--primary:hover,
.scorecard-settings-dialog .el-button--primary:focus { background-color: #522698; border-color: #522698; }

/* Cancel: neutral resting state with a purple-tinted hover instead of Element's
   default blue, so it fits this purple-themed dialog and matches the Cancel in
   the sibling Manager Classification Settings dialog. */
.scorecard-settings-dialog .el-button--default { color: #54545f; border-color: #e2e4ea; background: #fff; }
.scorecard-settings-dialog .el-button--default:hover,
.scorecard-settings-dialog .el-button--default:focus { color: #1a1a2e; border-color: #c9b8ee; background: #faf8fe; }

/* ---- date-filter preset menu (teleported to <body>) ---- */
/* Inherits the shared .happily-dropdown-popper chrome; this just lays out the
   check + label and marks the active preset in the brand purple. */
.sc-date-menu .sc-date-item { display: inline-flex; align-items: center; gap: 8px; }
.sc-date-menu .sc-date-item .el-icon-check { font-size: 13px; color: #6C30CC; visibility: hidden; }
.sc-date-menu .sc-date-item.sel .el-icon-check { visibility: visible; }
.sc-date-menu .sc-date-item.sel { color: #6C30CC; font-weight: 700; }

/* The on-demand range calendar, also teleported to <body>. */
.sc-range-popper.el-date-range-picker { border-radius: 10px; border: 1px solid #e2e4ea; box-shadow: 0 12px 30px rgba(17,24,39,.10); }
.sc-range-popper .el-date-table td.today span { color: #6C30CC; }
.sc-range-popper .el-date-table td.in-range div { background: #f2ecfb; }
.sc-range-popper .el-date-table td.start-date span,
.sc-range-popper .el-date-table td.end-date span { background: #6C30CC; }
</style>
